import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import usePlanStore from '../../../../../../store/planStore';
import {
  getBudgetTotals,
  isDdxPickedForAllCountries,
  isDdxNotApplicableForAllCountries,
} from '../helpers/helpers';
import { getDDXTotal } from '../../../MediaPlansTable/ComparedPlansModal/helpers';
import MissingDDXTooltip from '../MissingDDXTooltip/MissingDDXTooltip';

export interface TotalBudgetSummaryProps {
  isDDXLoading: boolean;
}

export function TotalBudgetSummary({
  isDDXLoading = false,
}: TotalBudgetSummaryProps) {
  const { t } = useTranslation();
  const { storedCountryForms } = usePlanStore();

  const { totalBudget, firstCurrency, totalTechCost } =
    getBudgetTotals(storedCountryForms);

  const totalDDX = getDDXTotal(storedCountryForms);

  const ddxNotApplicable =
    isDdxNotApplicableForAllCountries(storedCountryForms);

  return (
    <Grid
      container
      sx={{ p: '24px 46px 24px 15px', backgroundColor: 'tableBackground.main' }}
    >
      {/* TOTAL BUDGET */}
      <Grid
        item
        xs={12}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <Typography textTransform="uppercase" fontSize={18} fontWeight={700}>
          {t('mediaPlanning.totalBudget')}
        </Typography>
        <Typography fontWeight={700} fontSize={18}>
          {`${formatNumberWithSpaces(totalBudget.toFixed(0))} ${firstCurrency}`}
        </Typography>
      </Grid>

      {/* TECH COSTS */}
      <Grid
        item
        xs={12}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <Typography textTransform="uppercase" variant="body2" fontWeight={700}>
          {t('mediaPlanning.techCosts')}
        </Typography>
        <Typography fontWeight={700} variant="body2">
          {`${formatNumberWithSpaces(
            totalTechCost.toFixed(0)
          )} ${firstCurrency}`}
        </Typography>
      </Grid>

      {/* DDX — render ONLY if applicable */}
      {!ddxNotApplicable && (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Box>
            <Typography
              textTransform="uppercase"
              variant="body2"
              fontWeight={700}
            >
              DDX
            </Typography>

            {isDDXLoading && <CircularProgress size="small" />}

            {!isDDXLoading &&
              !isDdxPickedForAllCountries(storedCountryForms) && (
                <MissingDDXTooltip />
              )}
          </Box>

          <Typography fontWeight={700} variant="body2">
            {`${formatNumberWithSpaces(totalDDX.toFixed(0))} ${firstCurrency}`}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}
